import { useState, useEffect } from 'react';
import { db, collection, onSnapshot } from '../components/firebase';
import { SUBJECTS } from '../config/subjects';
import { getSubjectCollections } from '../utils/firebaseStats';

/**
 * Hook to fetch real-time statistics for all study materials
 */
const useStudyStats = () => {
    const [stats, setStats] = useState({
        notes: 0,
        pyqs: 0,
        books: 0,
        videos: 0,
        practicals: 0,
        loading: true
    });

    useEffect(() => {
        // 1. Collect all unique collection names
        const noteCollections = new Set();
        const videoCollections = new Set();

        SUBJECTS.forEach(subject => {
            const colls = getSubjectCollections(subject);
            if (colls) {
                noteCollections.add(colls.notes);
                videoCollections.add(colls.videos);
            }
        });

        const noteCollsArr = Array.from(noteCollections);
        const videoCollsArr = Array.from(videoCollections);

        // State to track counts from each collection
        // { collName: { notes: N, pyqs: P, books: B, practicals: PR, total: T } }
        const countsMap = new Map();

        // Initial setup for loading state
        setStats(prev => ({ ...prev, loading: true }));

        const unsubscribeFunctions = [];

        // Function to update global stats from countsMap
        const updateGlobalStats = () => {
            let totalNotes = 0;
            let totalPyqs = 0;
            let totalBooks = 0;
            let totalPracticals = 0;
            let totalVideos = 0;

            countsMap.forEach((val, key) => {
                if (key.endsWith('Videos')) {
                    totalVideos += val.total;
                } else {
                    totalNotes += val.notes;
                    totalPyqs += val.pyqs;
                    totalBooks += val.books;
                    totalPracticals += val.practicals;
                }
            });

            setStats({
                notes: totalNotes,
                pyqs: totalPyqs,
                books: totalBooks,
                videos: totalVideos,
                practicals: totalPracticals,
                loading: false
            });
        };

        // Subscribe to Notes collections
        noteCollsArr.forEach(collName => {
            const unsub = onSnapshot(collection(db, collName), (snapshot) => {
                const categoryCounts = {
                    notes: 0,
                    pyqs: 0,
                    books: 0,
                    practicals: 0,
                    total: snapshot.size
                };

                snapshot.docs.forEach(doc => {
                    const category = (doc.data().category || '').toLowerCase();
                    if (category === 'notes') categoryCounts.notes++;
                    else if (category === 'pyq') categoryCounts.pyqs++;
                    else if (category === 'books') categoryCounts.books++;
                    else if (category === 'practicals' || category === 'important') categoryCounts.practicals++;
                });

                countsMap.set(collName, categoryCounts);
                updateGlobalStats();
            }, (error) => {
                console.error(`Error subscribing to ${collName}:`, error);
                // Even on error, we mark it as processed with 0s to keep UI consistent
                countsMap.set(collName, { notes: 0, pyqs: 0, books: 0, practicals: 0, total: 0 });
                updateGlobalStats();
            });
            unsubscribeFunctions.push(unsub);
        });

        // Subscribe to Videos collections
        videoCollsArr.forEach(collName => {
            const unsub = onSnapshot(collection(db, collName), (snapshot) => {
                countsMap.set(collName, { total: snapshot.size });
                updateGlobalStats();
            }, (error) => {
                console.error(`Error subscribing to ${collName}:`, error);
                countsMap.set(collName, { total: 0 });
                updateGlobalStats();
            });
            unsubscribeFunctions.push(unsub);
        });

        return () => {
            unsubscribeFunctions.forEach(unsub => unsub());
        };
    }, []);

    return stats;
};

export default useStudyStats;
